import { alphabeticalOrder } from "@arenko-group/arenko-ui/dist/helpers";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { MEGA_WATT_CONVERSION_FACTOR } from "@/constants/common.constants";
import {
  setIsBMUnit,
  setNameplatePowerActive,
  setSelectedResource,
  setSelectedResourceId,
} from "@/features/scheduleEnergyRootSlice/scheduleEnergySlice";
import { getBaseUrl } from "@/helpers/fetch.helpers";
import { getJwtToken } from "@/helpers/storage.helpers";
import type {
  DeviceAttributes,
  DeviceData,
  DeviceReturnType,
} from "@/types/data";

export const devicesTransform = (data: DeviceData[]) => {
  const assets: { id: string; name: string }[] = data.map((d) => {
    return { name: d.name.split(".").pop() ?? "", id: d.id };
  });
  return alphabeticalOrder(assets, "name") as DeviceReturnType[];
};

export const entityExchangeApi = createApi({
  reducerPath: "entityExchangeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getBaseUrl("/entity-exchange/"),
    prepareHeaders: (headers) => {
      headers.set("authorization", `Bearer ${getJwtToken()}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    devices: builder.query<DeviceReturnType[], void>({
      query: () => ({
        url: "devices",
        params: { type: "Bess" },
      }),
      transformResponse: devicesTransform,
      async onCacheEntryAdded(
        _arg,
        { dispatch, cacheDataLoaded, getCacheEntry }
      ) {
        await cacheDataLoaded;
        const { data } = getCacheEntry();
        if (data) {
          // set first resource to be selected
          dispatch(setSelectedResource(data[0].name));
          dispatch(setSelectedResourceId(data[0].id));
        }
      },
    }),
    attributes: builder.query<DeviceAttributes, string>({
      query: (bessID) => ({
        url: `devices/${bessID}/attributes`,
      }),
      async onCacheEntryAdded(
        key,
        { dispatch, cacheDataLoaded, getCacheEntry }
      ) {
        await cacheDataLoaded;
        const { data } = getCacheEntry();
        if (data) {
          dispatch(
            setNameplatePowerActive({
              key,
              value:
                Number(data?.nameplatePowerActive) /
                  MEGA_WATT_CONVERSION_FACTOR || 50,
            })
          );
          const isBMUnit = data?.bmUnitId ? true : false;
          dispatch(setIsBMUnit({ key, value: isBMUnit }));
        }
      },
    }),
  }),
});

export const { useDevicesQuery, useAttributesQuery } = entityExchangeApi;
